import { EditOutline18Icon } from "@vygruppen/spor-icon-react";
import { format } from "date-fns";
import { GlobalEventResponse } from "features/CenterContent/RoleContent/GlobalEvents/types/responseSchema";
import { depublishInternalMessage } from "features/CenterContent/RoleContent/InternalMessage/utils/queries";
import { WithInternalMessages } from "features/CenterContent/RoleContent/InternalMessage/utils/withInternalMessages";
import { Badges } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/Badges";
import { globalEventsQueryKey } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/useGlobalInformation";
import {
  getDisplayDurationFromVisibility,
  sortByVisibility,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/utils";
import { MessageInternal } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/Version";
import { DepublishInformation } from "features/OperationalInformation/DepublishInformation";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { SegmentedPanel } from "shared/components/SegmentedPanel";
import { HStack, IconButton, Stack } from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import { useDropsRole } from "stores/useDropsRole";
import { css, useTheme } from "styled-components";

type GlobalEventsProps = {
  data: WithInternalMessages<GlobalEventResponse>[];
  onEditClick: (event: WithInternalMessages<GlobalEventResponse>) => void;
};

export const GlobalEventsList = ({ data, onEditClick }: GlobalEventsProps) => {
  const theme = useTheme();
  const { isVaktleder } = useDropsRole();
  if (data.length === 0)
    return <NoDataMessage reason="Det er ikke registrert noen hendelser" />;
  return (
    <Stack gap="18px">
      {data.sort(sortByVisibility).map((event) => {
        const text =
          event.customTexts.length > 0
            ? event.customTexts[0].texts.NOB
            : { title: "", description: "" };
        return (
          <Stack gap={0} key={event.uuid}>
            <SegmentedPanel
              panelStyle={css`
                background: ${theme.colorBackgroundTertiary};
                border-radius: 12px;
                padding: 18px;
                gap: 6px;
              `}
              dashedLineVariant="tertiary"
            >
              <Stack>
                <HStack alignItems="center" justifyContent="space-between">
                  <Text
                    bold
                    truncated
                    style={{
                      flexShrink: "2",
                    }}
                  >
                    {text?.title}
                  </Text>
                  {isVaktleder() && (
                    <HStack minWidth="fit-content">
                      <DepublishInformation
                        uuid={event.uuid}
                        disabled={false}
                        queryKey={globalEventsQueryKey(event.countryCode)}
                        depublishInternalMessage={() =>
                          depublishInternalMessage(event.internalMessages)
                        }
                      />
                      <IconButton
                        onClick={() => onEditClick(event)}
                        size="sm"
                        aria-label="Rediger"
                        variant="tertiary"
                        icon={<EditOutline18Icon />}
                      />
                    </HStack>
                  )}
                </HStack>
                <Badges
                  eventCategory="global"
                  displayDuration={getDisplayDurationFromVisibility(
                    event.visibility,
                  )}
                />
                <Text>{text?.description}</Text>
                <Text secondary>
                  {format(
                    event.updatedAt.toDate(),
                    "'Sist oppdatert' dd.MM.yyyy 'kl.' HH:mm",
                  )}
                </Text>
              </Stack>
              {event.internalMessages && event.internalMessages.length > 0 && (
                <Stack pb="6px !important">
                  <MessageInternal internalMessages={event.internalMessages} />
                </Stack>
              )}
            </SegmentedPanel>
          </Stack>
        );
      })}
    </Stack>
  );
};
